// Discovery-tag taxonomy for recipe stubs.
//
// Each tag is one way the system might recommend a stub: an
// occasion ("July 4th"), a recurring slot ("Sunday Brunch"), a
// vibe ("cookout", "cold-weather"), or an audience ("kids").
//
// A stub can fly multiple tags. The occasion engine
// (src/lib/occasionContext.js) filters stubs by tag intersection
// at recommendation time, weighted by:
//   - `seasonal`  — fixed-date or dynamic-date occasions (Easter,
//                   July 4th, Super Bowl Sunday, Thanksgiving).
//                   `leadDays` is how many days BEFORE the date the
//                   tag starts surfacing.
//   - `recurring` — day-of-week + time-of-day matches (Sunday
//                   morning brunch, weeknight dinner).
//   - neither     — vibe tags that surface anytime ("cookout" as a
//                   summer-leaning generic, "rainy-day" mood, etc.).
//
// To add a new tag: append a row here, then create stubs in
// `recipe_stubs/<bucket>.js` that include the tag in
// `discoveryTags`. The recommendation hook picks them up
// automatically — no resolver changes needed.

// ── Dynamic-date helpers ────────────────────────────────────────
//
// Holidays whose date moves year-to-year need a computed function.
// Each function returns a Date for the given year (or the next
// occurrence if `year` is omitted).

// Easter Sunday — Anonymous Gregorian algorithm (Meeus / Jones /
// Butcher). Works for any year ≥ 1583.
function easterDate(year) {
  const y = year != null ? year : new Date().getFullYear();
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(y, month - 1, day);
}

// Super Bowl Sunday — 2nd Sunday of February (CBA rule since 2022;
// before that it was the 1st Sunday, sometimes the LAST Sunday of
// January). The 2nd-Sunday-of-February rule covers 2022+.
function superbowlSunday(year) {
  const y = year != null ? year : new Date().getFullYear();
  // Find first Sunday of February
  const feb1 = new Date(y, 1, 1);
  const firstSundayOffset = (7 - feb1.getDay()) % 7;
  return new Date(y, 1, 1 + firstSundayOffset + 7); // +7 for SECOND Sunday
}

// Memorial Day — last Monday of May.
function memorialDay(year) {
  const y = year != null ? year : new Date().getFullYear();
  const may31 = new Date(y, 4, 31);
  const offsetBack = (may31.getDay() + 6) % 7; // days back to Monday
  return new Date(y, 4, 31 - offsetBack);
}

// Labor Day — first Monday of September.
function laborDay(year) {
  const y = year != null ? year : new Date().getFullYear();
  const sep1 = new Date(y, 8, 1);
  const offset = (8 - sep1.getDay()) % 7;
  return new Date(y, 8, 1 + offset);
}

// Thanksgiving — fourth Thursday of November (US).
function thanksgivingDay(year) {
  const y = year != null ? year : new Date().getFullYear();
  const nov1 = new Date(y, 10, 1);
  const firstThursdayOffset = (4 - nov1.getDay() + 7) % 7;
  return new Date(y, 10, 1 + firstThursdayOffset + 21); // +21 for FOURTH Thursday
}

// ── Tag taxonomy ────────────────────────────────────────────────
export const DISCOVERY_TAGS = {
  // ── Seasonal / holiday (fixed-date or dynamic) ────────────────
  superbowl: {
    label: "Super Bowl Sunday",
    emoji: "🏈",
    seasonal: { dynamicDate: superbowlSunday, leadDays: 14 },
  },
  valentines: {
    label: "Valentine's",
    emoji: "💝",
    seasonal: { month: 1, day: 14, leadDays: 7 },
  },
  easter: {
    label: "Easter",
    emoji: "🐰",
    seasonal: { dynamicDate: easterDate, leadDays: 14 },
  },
  memorialDay: {
    label: "Memorial Day",
    emoji: "🇺🇸",
    seasonal: { dynamicDate: memorialDay, leadDays: 7 },
  },
  july4th: {
    label: "4th of July",
    emoji: "🎆",
    seasonal: { month: 6, day: 4, leadDays: 7 },
  },
  laborDay: {
    label: "Labor Day",
    emoji: "🏖️",
    seasonal: { dynamicDate: laborDay, leadDays: 7 },
  },
  halloween: {
    label: "Halloween",
    emoji: "🎃",
    seasonal: { month: 9, day: 31, leadDays: 7 },
  },
  thanksgiving: {
    label: "Thanksgiving",
    emoji: "🦃",
    seasonal: { dynamicDate: thanksgivingDay, leadDays: 21 },
  },
  christmas: {
    label: "Christmas",
    emoji: "🎄",
    seasonal: { month: 11, day: 25, leadDays: 21 },
  },
  newYearsEve: {
    label: "New Year's Eve",
    emoji: "🥂",
    seasonal: { month: 11, day: 31, leadDays: 7 },
  },

  // ── Recurring (day-of-week + time-window) ─────────────────────
  "sunday-morning": {
    label: "Sunday Morning",
    emoji: "🥐",
    recurring: { dow: [0], timeWindow: [6, 12] },
  },
  "sunday-dinner": {
    label: "Sunday Dinner",
    emoji: "🍖",
    recurring: { dow: [0], timeWindow: [16, 20] },
  },
  brunch: {
    label: "Brunch",
    emoji: "🥞",
    recurring: { dow: [0, 6], timeWindow: [9, 13] },
  },
  weeknight: {
    label: "Weeknight",
    emoji: "⚡",
    recurring: { dow: [1, 2, 3, 4], timeWindow: [16, 21] },
  },
  "date-night": {
    label: "Date Night",
    emoji: "🍷",
    recurring: { dow: [5, 6], timeWindow: [17, 22] },
  },
  "late-night": {
    label: "Late Night",
    emoji: "🌙",
    recurring: { dow: [5, 6], timeWindow: [22, 28] }, // wraps past midnight
  },

  // ── Vibe / mood tags (no date math — always available) ────────
  cookout: { label: "Cookout", emoji: "🔥" },
  bbq: { label: "BBQ", emoji: "🍖" },
  "rainy-day": { label: "Rainy Day", emoji: "☔" },
  "cold-weather": { label: "Cold Weather", emoji: "❄️" },
  "summer-grill": { label: "Summer Grill", emoji: "☀️" },
  "game-day": { label: "Game Day", emoji: "🍻" },
  kids: { label: "Kid-Friendly", emoji: "🧒" },
  "meal-prep": { label: "Meal Prep", emoji: "📦" },
  "comfort-food": { label: "Comfort Food", emoji: "🍲" },
};

// Exposed for tests / lower-level scheduling logic. The
// occasionContext.js engine and any future pg_cron push scheduler
// both rely on these date helpers.
export const DATE_HELPERS = {
  easterDate,
  superbowlSunday,
  memorialDay,
  laborDay,
  thanksgivingDay,
};
